import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { ClientListRow } from "@themislex/shared";
import { useApi } from "../../api/hooks";
import { Badge, Button, ErrorNote, Money, PageHeader, Spinner } from "../../components/ui";
import { ClientFormModal } from "../../components/ClientFormModal";

export default function Clients() {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const path = debounced
    ? `/api/clients?q=${encodeURIComponent(debounced)}`
    : "/api/clients";
  const { data, error, loading, refetch } = useApi<{ clients: ClientListRow[] }>(path);

  return (
    <>
      <PageHeader
        title="Clients"
        subtitle="Every client of the practice, with balances at a glance."
        actions={
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            Add client
          </Button>
        }
      />

      <div className="card mb-6 p-4">
        <label className="label" htmlFor="client-search">
          Search
        </label>
        <input
          id="client-search"
          className="input"
          type="search"
          placeholder="Name, email or phone…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && (
        <div className="flex justify-center py-16 text-muted">
          <Spinner className="h-6 w-6" />
        </div>
      )}

      {data && (
        <div className="card overflow-x-auto p-2">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Phone</th>
                <th scope="col">City</th>
                <th scope="col" className="!text-right">
                  Matters
                </th>
                <th scope="col" className="!text-right">
                  Balance
                </th>
                <th scope="col">Login</th>
              </tr>
            </thead>
            <tbody>
              {data.clients.map((client) => (
                <tr key={client.id}>
                  <td className="font-medium">
                    <Link
                      to={`/desk/clients/${client.id}`}
                      className="text-accent-text hover:underline"
                    >
                      {client.name}
                    </Link>
                  </td>
                  <td>{client.email}</td>
                  <td className="whitespace-nowrap">{client.phone ?? "—"}</td>
                  <td>{client.city ?? "—"}</td>
                  <td className="text-right">{client.matterCount}</td>
                  <td className="whitespace-nowrap text-right font-medium">
                    {client.balanceMinor > 0 ? (
                      <Money minor={client.balanceMinor} />
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <Badge variant={client.setupPending ? "warning" : "success"}>
                      {client.setupPending ? "Setup pending" : "Active"}
                    </Badge>
                  </td>
                </tr>
              ))}
              {data.clients.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-muted">
                    No clients match this search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <ClientFormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={() => refetch()}
      />
    </>
  );
}
